/**
 * Data layer for `apptrove-pricing` — mobile MMP pricing quotes.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * Backs onto POST/GET /api/v1/apptrove-pricing/quotes + status/price endpoints.
 */

export type QuoteStatus = 'requested' | 'quoted' | 'accepted' | 'declined';
export type Platform = 'ios' | 'android' | 'web' | 'ctv';
export type ScaleBand = 'indie' | 'growth' | 'scale' | 'enterprise';

export interface AddonFlags {
	skan?: boolean;
	ctv?: boolean;
	pltv?: boolean;
	incrementality?: boolean;
	s2s?: boolean;
}

export interface Quote {
	id: string;
	tenant_id: string;
	est_installs_monthly: number;
	est_events_monthly: number;
	est_mau: number;
	est_mtu: number;
	platforms: Platform[];
	addon_skan: boolean;
	addon_ctv: boolean;
	addon_pltv: boolean;
	addon_incrementality: boolean;
	addon_s2s: boolean;
	contact_email: string;
	notes: string | null;
	status: QuoteStatus;
	quoted_amount_minor: number | null;
	quoted_currency: string | null;
	created_at: number;
	updated_at: number;
	scale_band: ScaleBand;
	addon_surcharge_bps: number;
}

export interface CreateQuoteBody {
	est_installs_monthly: number;
	est_events_monthly: number;
	est_mau: number;
	est_mtu: number;
	platforms: Platform[];
	addons?: AddonFlags;
	contact_email: string;
	notes?: string | null;
}

const BASE = '/api/v1/apptrove-pricing';

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === 'object' &&
			data !== null &&
			'error' in data &&
			typeof (data as { error: unknown }).error === 'string'
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listQuotes(): Promise<Quote[]> {
	const res = await fetch(`${BASE}/quotes`, {
		method: 'GET',
		credentials: 'include',
		headers: { accept: 'application/json' },
	});
	return parseOrThrow<Quote[]>(res);
}

export async function createQuote(body: CreateQuoteBody): Promise<Quote> {
	const res = await fetch(`${BASE}/quotes`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			accept: 'application/json',
			'content-type': 'application/json',
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<Quote>(res);
}

export async function updateQuoteStatus(
	id: string,
	status: QuoteStatus,
): Promise<Quote> {
	const res = await fetch(
		`${BASE}/quotes/${encodeURIComponent(id)}/status`,
		{
			method: 'PATCH',
			credentials: 'include',
			headers: {
				accept: 'application/json',
				'content-type': 'application/json',
			},
			body: JSON.stringify({ status }),
		},
	);
	return parseOrThrow<Quote>(res);
}

export const apptrovePricingQueryKeys = {
	all: ['apptrove-pricing'] as const,
	list: () => [...apptrovePricingQueryKeys.all, 'quotes'] as const,
	detail: (id: string) => [...apptrovePricingQueryKeys.all, 'quotes', id] as const,
};
